/**
 * Login page - shown when user is not authenticated
 */
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { GiHeartBeats } from 'react-icons/gi';
import { IoLogoGithub, IoLogoGoogle } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth';
import Spinner from '@/components/Spinner/Spinner';

export default function LoginPage(): ReactElement {
  const { signIn, status } = useAuth();
  const navigate = useNavigate();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to home when authenticated
  useEffect(() => {
    if (status === 'authenticated') {
      navigate('/', { replace: true });
    }
  }, [status, navigate]);

  const handleSignIn = async (provider: 'google' | 'github') => {
    setIsSigningIn(true);
    setError(null);
    try {
      await signIn(provider);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setIsSigningIn(false);
    }
  };

  if (status === 'loading' || status === 'authenticated') {
    return (
      <div className="bg-void flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-void flex min-h-screen flex-col items-center justify-center px-4">
      {/* Logo and title */}
      <div className="mb-12 text-center">
        <img src="/favicon.svg" alt="MtgCraft" className="mx-auto mb-6 h-20 w-20" />
        <h1 className="font-heading text-pearl text-4xl font-bold">MtgCraft</h1>
        <p className="text-silver mt-2">Manage your Magic: The Gathering collection</p>
      </div>

      {/* Login card */}
      <div className="border-storm bg-obsidian shadow-void/50 w-full max-w-sm rounded-xl border p-8 shadow-2xl">
        <h2 className="text-pearl mb-6 text-center text-xl font-semibold">Sign in to continue</h2>

        {error && <div className="bg-danger/10 text-danger mb-4 rounded-lg px-4 py-3 text-sm">{error}</div>}

        <div className="flex flex-col gap-3">
          <button
            onClick={() => handleSignIn('google')}
            disabled={isSigningIn}
            className="border-storm bg-slate text-pearl hover:bg-storm flex items-center justify-center gap-3 rounded-lg border px-4 py-3 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSigningIn ? (
              <Spinner size="sm" />
            ) : (
              <>
                <IoLogoGoogle className="h-5 w-5 text-[#4285F4]" />
                Continue with Google
              </>
            )}
          </button>

          <button
            onClick={() => handleSignIn('github')}
            disabled={isSigningIn}
            className="border-storm bg-slate text-pearl hover:bg-storm flex items-center justify-center gap-3 rounded-lg border px-4 py-3 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSigningIn ? (
              <Spinner size="sm" />
            ) : (
              <>
                <IoLogoGithub className="h-5 w-5" />
                Continue with GitHub
              </>
            )}
          </button>
        </div>

        <p className="text-ash mt-6 text-center text-xs">By signing in, you agree to our terms of service and privacy policy.</p>
      </div>

      {/* The Game Companion works without an account */}
      <Link
        to="/companion"
        className="border-storm text-pearl hover:bg-slate mt-6 flex w-full max-w-sm items-center justify-center gap-3 rounded-lg border px-4 py-3 font-medium transition-colors"
      >
        <GiHeartBeats className="h-5 w-5" />
        Open Game Companion
      </Link>
      <p className="text-silver mt-2 text-xs">Life tracker for in-person games, no sign-in needed</p>

      {/* Footer */}
      <p className="text-ash mt-8 text-sm">
        Card data provided by{' '}
        <a
          href="https://scryfall.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-aether hover:text-aether-glow transition-colors"
        >
          Scryfall
        </a>
      </p>
    </div>
  );
}
